import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface AggregatedItem {
  type: string;
  count: number;
}

interface TypeBarChartProps {
  title: string;
  description: string;
  data: AggregatedItem[];
  yearSuffix?: string;
  seriesLabel: string;
  color: string;
  height?: number;
  className?: string;
}

export default function TypeBarChart({
  title,
  description,
  data,
  yearSuffix,
  seriesLabel,
  color,
  height = 360,
  className,
}: TypeBarChartProps) {
  const chartConfig = useMemo<ChartConfig>(
    () => ({
      count: { label: seriesLabel, color },
    }),
    [seriesLabel, color],
  );

  return (
    <Card className={`border-ctu-border shadow-none ${className ?? ''}`}>
      <CardContent className="flex flex-col gap-3 px-6 pt-5 pb-4">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {title}
            {yearSuffix && (
              <span className="ml-2 font-normal normal-case tracking-normal text-ctu-text-muted/80">
                · {yearSuffix}
              </span>
            )}
          </span>
          <span className="text-xs text-ctu-text-muted leading-snug">
            {description}
          </span>
        </div>

        {data.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-ctu-text-muted">
            —
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-auto w-full"
            style={{ height }}
          >
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
              barCategoryGap={3}
            >
              <CartesianGrid horizontal={false} strokeDasharray="3 3" />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tickMargin={4}
                allowDecimals={false}
              />
              <YAxis
                dataKey="type"
                type="category"
                tickLine={false}
                axisLine={false}
                width={210}
                tickMargin={4}
                fontSize={11}
                interval={0}
              />
              <ChartTooltip
                cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    nameKey="type"
                    labelFormatter={(value) => String(value)}
                  />
                }
              />
              <Bar
                dataKey="count"
                fill="var(--color-count)"
                radius={[0, 3, 3, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
